import React, { useMemo, useState } from 'react';
import { notifyAdmin, submitSupportMessage, uploadSupportAttachment } from '../supabaseClient';
import { serializeSupportMessageContent } from '../supportMessageContent';
import './SupportMessageModal.css';

const MAX_FILES = 10;

const SupportMessageModal = ({
  user = null,
  source = 'dashboard',
  triggerLabel = 'Message Servsolo',
  triggerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: user?.email || '', subject: '', message: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  const normalizedEmail = useMemo(() => form.email.trim().toLowerCase(), [form.email]);

  const closeModal = () => {
    setIsOpen(false);
    setError('');
    setSuccess('');
  };

  const handleFiles = (selected) => {
    const merged = [...files, ...Array.from(selected)].slice(0, MAX_FILES);
    setFiles(merged);
    const urls = merged.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setPreviews(urls);
  };

  const removeFile = (index) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    setPreviews(next.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name.trim() || !normalizedEmail || !form.subject.trim() || !form.message.trim()) {
      setError('Please complete Name, Email, Subject, and Message.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const uploadedAttachments = files.length > 0
        ? await Promise.all(files.map(f => uploadSupportAttachment(f)))
        : [];

      const messageRecord = await submitSupportMessage({
        created_by_user_id: user?.id || null,
        from_name: form.name.trim(),
        from_email: normalizedEmail,
        subject: form.subject.trim(),
        message: serializeSupportMessageContent(form.message.trim(), uploadedAttachments),
        source,
      });

      try {
        const emailResult = await notifyAdmin({
          kind: 'support-message',
          email: normalizedEmail,
          name: form.name.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          metadata: {
            source,
            messageId: messageRecord.id,
            attachments: uploadedAttachments,
          },
        });
        if (emailResult?.confirmation?.delivered) {
          setSuccess('Message sent. A confirmation email has been sent to you.');
        } else {
          setSuccess('Message sent to Servsolo admin.');
        }
      } catch {
        setSuccess('Message sent to Servsolo admin.');
      }

      setForm({ name: '', email: user?.email || '', subject: '', message: '' });
      setFiles([]);
      setPreviews([]);
    } catch (submitError) {
      setError(submitError.message || 'Failed to send message.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" className={triggerClassName} onClick={() => setIsOpen(true)}>{triggerLabel}</button>

      {isOpen && (
        <div className="support-message-overlay" onClick={closeModal}>
          <div className="support-message-card" onClick={(e) => e.stopPropagation()}>
            <h2>Send Message</h2>
            <p>Send a message to Servsolo admin. It will be tracked in the admin inbox.</p>
            <form onSubmit={handleSubmit}>
              <div className="support-message-group">
                <label>Name *</label>
                <input value={form.name} onChange={(e) => setForm(c => ({ ...c, name: e.target.value }))} placeholder="Your name" autoFocus />
              </div>
              <div className="support-message-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm(c => ({ ...c, email: e.target.value }))} placeholder="you@example.com" disabled={Boolean(user?.email)} />
              </div>
              <div className="support-message-group">
                <label>Subject *</label>
                <input value={form.subject} onChange={(e) => setForm(c => ({ ...c, subject: e.target.value }))} placeholder="What do you need help with?" />
              </div>
              <div className="support-message-group">
                <label>Message *</label>
                <textarea value={form.message} onChange={(e) => setForm(c => ({ ...c, message: e.target.value }))} rows={4} placeholder="Write your message" />
              </div>
              <div className="support-message-group">
                <label>Attachments <span className="support-message-file-hint">(up to {MAX_FILES} photos or files)</span></label>
                <label className="support-message-file-btn">
                  + Add files
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFiles(e.target.files)}
                    disabled={files.length >= MAX_FILES}
                  />
                </label>
                {files.length > 0 && (
                  <div className="support-message-thumbs">
                    {files.map((f, i) => (
                      <div key={i} className="support-message-thumb">
                        {previews[i]
                          ? <img src={previews[i]} alt={f.name} />
                          : <div className="support-message-thumb-file">{f.name.split('.').pop().toUpperCase()}</div>
                        }
                        <button type="button" className="support-message-thumb-remove" onClick={() => removeFile(i)}>×</button>
                        <div className="support-message-thumb-name">{f.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {error && <div className="support-message-error">{error}</div>}
              {success && <div className="support-message-success">{success}</div>}
              <div className="support-message-actions">
                <button type="button" className="support-message-cancel" onClick={closeModal}>Close</button>
                <button type="submit" className="support-message-submit" disabled={submitting}>{submitting ? 'Sending...' : 'Send Message'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportMessageModal;
